import fetchJSON from '../fetchJSON';
import {uniq, sortBy, find, memoize} from 'lodash';

const getMapSources = memoize(async (user: User | undefined) => {
    const nonPublicSources = Boolean(user?.canAccessNonPublicSources);
    const url = `/mapsources/v2/gaiagps-ng-2019/?nonPublicSources=${nonPublicSources}`;
    const mapsources = await fetchJSON.memoized(url, {
        credentials: 'same-origin',
    });
    return mapsources;
});

// Get default layers to display in users layers menu and on the map
export const fetchDefaultUserLayers = memoize(
    async (user: User | undefined): Promise<UserLayer[] | undefined> => {
        const mapsources = await getMapSources(user);
        const language =
            (window.navigator as any).userLanguage || window.navigator.language;
        const languageKey = language === 'en-US' ? 'US' : 'default';
        const membershipKey = user?.hasSubscription ? 'pro_sources' : 'sources';

        const defaultType = mapsources.defaults[languageKey].default_source;
        const types = uniq([
            defaultType,
            ...mapsources.defaults[languageKey][membershipKey],
        ]);

        const defaultUserLayers = sortBy(
            types.map((type: string) => ({
                isVisible: defaultType === type,
                layer: {
                    type,
                    opacity: 1,
                },
            })),
            'isVisible'
        );

        if (!find(defaultUserLayers, ({isVisible}) => isVisible)) {
            console.error('No default user layer is visible.');
            defaultUserLayers[0].isVisible = true;
        }
        return defaultUserLayers;
    }
);

export interface DefaultSources {
    sources: string[];
    huntingStates: HuntingStates[];
}
export interface HuntingStates {
    state: string;
    country: string | undefined;
    sources: string[];
}

// Get recommended default layers for activities
export const fetchDefaultLayers = memoize(
    async (
        user: User | undefined,
        activitiesList: string[]
    ): Promise<DefaultSources> => {
        const defaultSources: DefaultSources = {
            sources: [],
            huntingStates: [],
        };
        if (activitiesList.length === 0) return defaultSources;
        const mapsources = await getMapSources(user);
        if (!mapsources) return defaultSources;
        const language =
            (window.navigator as any).userLanguage || window.navigator.language;
        const languageKey =
            language === 'en-US' ? 'US' : language === 'en-CA' ? 'CA' : '';

        const types = (activity: string) => {
            const defaultActivity = mapsources.defaults_activity[activity];
            if (!defaultActivity) return;
            defaultActivity.forEach((layersDefault: LayerDefaultsObject) => {
                if (!layersDefault.state) {
                    defaultSources.sources.push(...layersDefault.pro_sources);
                    defaultSources.sources.push(...layersDefault.sources);
                } else if (layersDefault.activity === 'hunting') {
                    if (languageKey && languageKey !== layersDefault.country)
                        return;
                    defaultSources.huntingStates.push({
                        state: layersDefault.state,
                        country: layersDefault.country,
                        sources: layersDefault.pro_sources,
                    });
                }
            });
        };

        activitiesList.forEach((activity) => types(activity));
        defaultSources.sources = uniq(defaultSources.sources);
        return defaultSources;
    },
    (...args) => JSON.stringify(args)
);
